import { useState, type InputEvent, type SubmitEvent } from 'react'
import { getCountryDataList } from 'countries-list'
import {
  createUserWithEmailAndPassword,
  OAuthCredential,
  sendEmailVerification,
  signOut,
} from 'firebase/auth'
import { Link, useLocation } from 'react-router'
import { authErrorMessage } from '../lib/authErrors'
import { signInWithGoogle, signInWithGoogleCredential } from '../lib/googleAuth'
import { getLastUsedMethod, rememberLastUsedMethod } from '../lib/lastUsedMethod'
import { useAuth } from '../providers/auth/useAuth'
import { AccountService } from '../client'
import AuthCard from '../components/auth/AuthCard'
import AuthField from '../components/auth/AuthField'
import AuthSelect from '../components/auth/AuthSelect'
import GoogleSignIn from '../components/auth/GoogleSignIn'
import { apiErrorMessage, hasStatus } from '../lib/api'
import { auth } from '../lib/firebase'
import { collectErrors, type FieldErrors } from '../lib/formErrors'

const MESSAGES = {
  email: 'Enter a valid email address.',
  password: 'Password must be at least 6 characters.',
  name: 'Enter your name.',
  phone: 'Enter a phone number like +1 555 123 4567.',
  brand: 'Enter your brand name.',
  website: 'Enter a website like yourbrand.com',
}

const ROLES = ['Founder or owner', 'Marketing lead', 'Performance marketer', 'Content creator', 'Agency', 'Other']

const COUNTRY_CODES = new Map(
  getCountryDataList()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ name, iso2 }) => [name, iso2.toLowerCase()]),
)

const SUBMIT =
  'h-11 w-full rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 text-[15px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90 disabled:opacity-60'

type NewAccount =
  | { method: 'email'; email: string; password: string }
  | { method: 'google'; credential: OAuthCredential; name: string | null }
  | { method: 'resume' }

function googleHandoff(state: unknown): NewAccount | null {
  const handoff = state as { googleCredential?: object; googleName?: string | null } | null
  const credential = handoff?.googleCredential && OAuthCredential.fromJSON(handoff.googleCredential)
  return credential ? { method: 'google', credential, name: handoff?.googleName ?? null } : null
}

export default function Signup() {
  const location = useLocation()
  const { user, refreshAccount } = useAuth()
  const [newAccount, setNewAccount] = useState<NewAccount | null>(
    () => googleHandoff(location.state) ?? (user ? { method: 'resume' } : null),
  )
  const [step, setStep] = useState<'account' | 'details'>(newAccount ? 'details' : 'account')
  const [name, setName] = useState(
    () => (newAccount?.method === 'google' ? newAccount.name : user?.displayName) ?? '',
  )
  const [role, setRole] = useState('')
  const [country, setCountry] = useState('United States')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function clearFieldError(event: InputEvent<HTMLFormElement>) {
    const { name } = event.target as HTMLInputElement
    setFieldErrors((current) => ({ ...current, [name]: '' }))
  }

  function continueToDetails(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const invalid = collectErrors(form, MESSAGES)
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    const fields = new FormData(form)
    setNewAccount({ method: 'email', email: String(fields.get('email')), password: String(fields.get('password')) })
    setError('')
    setStep('details')
  }

  async function continueWithGoogle() {
    const signup = await signInWithGoogle()
    if (!signup) return
    setNewAccount({ method: 'google', ...signup })
    setName(signup.name ?? '')
    setStep('details')
  }

  async function createAccount(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!newAccount) return

    const invalid = collectErrors(event.currentTarget, MESSAGES)
    if (!role) invalid.role = 'Select your role.'
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    const fields = new FormData(event.currentTarget)
    setError('')
    setSubmitting(true)

    try {
      if (newAccount.method === 'google') {
        await signInWithGoogleCredential(newAccount.credential)
      } else if (newAccount.method === 'email') {
        const created = await createUserWithEmailAndPassword(
          auth,
          newAccount.email,
          newAccount.password,
        )
        // A Google sign-in is already verified; a password one is not, and the
        // backend refuses everything past setup until it is.
        await sendEmailVerification(created.user)
        rememberLastUsedMethod('email')
      }
    } catch (err) {
      setError(
        newAccount.method === 'google'
          ? "We couldn't finish signing you up with Google. Please continue with Google again."
          : authErrorMessage(err),
      )
      setStep('account')
      setSubmitting(false)
      return
    }

    try {
      await AccountService.setup({
        brand: {
          name: String(fields.get('brand')),
          website: String(fields.get('website')),
          country: COUNTRY_CODES.get(country),
        },
        contact: { name, phone: String(fields.get('phone')), role },
      })
    } catch (err) {
      if (!hasStatus(err, 409)) {
        setError(apiErrorMessage(err))
        setSubmitting(false)
        return
      }
    }

    await refreshAccount()
  }

  const onDetails = step === 'details'

  return (
    <AuthCard
      title={onDetails ? 'Tell us about yourself' : 'Create your free account'}
      subtitle={onDetails ? 'A few details to set up your workspace.' : 'Get started in under a minute.'}
      footer={
        onDetails ? (
          newAccount?.method === 'resume' ? (
            <button type="button" onClick={() => signOut(auth)} className="font-medium text-purple-700 hover:text-purple-800">
              Log out
            </button>
          ) : (
            <button type="button" onClick={() => setStep('account')} className="font-medium text-purple-700 hover:text-purple-800">
              ← Back
            </button>
          )
        ) : (
          <>
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-purple-700 hover:text-purple-800">
              Log in
            </Link>
          </>
        )
      }
    >
      {!onDetails && <GoogleSignIn signIn={continueWithGoogle} onError={setError} />}

      <form noValidate hidden={onDetails} onSubmit={continueToDetails} onInput={clearFieldError} className="space-y-4">
        <AuthField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Your work email"
          lastUsed={getLastUsedMethod() === 'email'}
          error={fieldErrors.email}
          required
        />
        <AuthField
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 6 characters"
          minLength={6}
          error={fieldErrors.password}
          required
        />

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button type="submit" className={SUBMIT}>
          Continue
        </button>
      </form>

      <form noValidate hidden={!onDetails} onSubmit={createAccount} onInput={clearFieldError} className="space-y-4">
        <AuthField
          label="Your name"
          name="name"
          autoComplete="name"
          placeholder="Jane Cooper"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrors.name}
          required
        />
        <AuthField
          label="Phone number"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+1 555 123 4567"
          pattern="[0-9+\(\)\-\s]{6,}"
          error={fieldErrors.phone}
          required
        />
        <AuthField
          label="Brand name"
          name="brand"
          autoComplete="organization"
          placeholder="Acme"
          error={fieldErrors.brand}
          required
        />
        <AuthField
          label="Brand website"
          name="website"
          inputMode="url"
          autoComplete="url"
          placeholder="yourbrand.com"
          pattern="(https?://)?([A-Za-z0-9\-]+\.)+[A-Za-z]{2,}(/.*)?"
          error={fieldErrors.website}
          required
        />
        <AuthSelect
          label="Role"
          placeholder="Select your role"
          options={ROLES}
          value={role}
          error={fieldErrors.role}
          onChange={(value) => {
            setRole(value)
            setFieldErrors((current) => ({ ...current, role: '' }))
          }}
        />
        <AuthSelect
          label="Country"
          placeholder="Select your country"
          options={[...COUNTRY_CODES.keys()]}
          value={country}
          onChange={setCountry}
        />

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} className={SUBMIT}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthCard>
  )
}
